import { PureComponent } from 'react';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';

import PricingPlans from '../PricingPlans';
import PricingPlansTable from '../PricingPlansTable';
import FAQComponent from '../FAQ';
import {
  FAQ,
  premiumPlans,
  freePlan,
  planTableHeader,
} from '../../../data/pricingPlansData';
import PriceCurrencyProvider from '../PriceCurrencyProvider';

import PlayVideoComponent from '../../PlayVideoComponent';
import SubscriptionsManagement from '../SubscriptionsManagement';
import InsightsPricing from '../InsightsPricing';
import CFDropdown from '../../Shared/CFDropdown';

import { SILVER_PLAN, GOLD_PLAN } from '../../../data/subscriptionPlans';
import SubscriptionsSvc from '../../../services/SubscriptionsSvc';
import * as authentication from '../../../actions/authentication.actions';

import iconSummary from '../../../assets/investing/icon-summary-grey.svg';
import iconActiveSummary from '../../../assets/investing/icon-summary-blue.svg';
import iconTable from '../../../assets/investing/icon-table-grey.svg';
import iconActiveTable from '../../../assets/investing/icon-table-blue.svg';

import Styles from './styles.module.scss';

export class PricingClient extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeFAQid: null,
      isSummaryTabActive: true,
      userPlan: null,
      userPlanType: null,
      loading: true,
      premiumPlansData: [],
      premiumTablePlansData: [],
      currencyOptions: [
        {
          value: 'usd',
          label: 'USD',
          selected: true,
        },
        {
          value: 'gbp',
          label: 'GBP',
          selected: false,
        },
      ],
      selectedCurrency: 'usd',
    };
    autoBind(this);
  }

  componentDidMount() {
    const { piwikEnabled, history } = this.props;
    this.setState({
      premiumPlansData: [freePlan, ...premiumPlans],
      premiumTablePlansData: [freePlan, ...premiumPlans],
    }, () => {
      this.getUserPlan();
    });

    if (window.innerWidth > 767) {
      this.setState({
        isSummaryTabActive: history.location.hash !== '#table',
      });
    }
    if (window.navigator.language.toLowerCase().startsWith('en-gb')) {
      this.setCurrency('gbp');
      this.setState(({ currencyOptions }) => ({
        currencyOptions: currencyOptions.map((option) => ({
          ...option,
          selected: option.value === 'gbp',
        })),
      }));
    }

    if (piwikEnabled) _paq.push(['trackPageView']);
  }

  setCurrency(currency) {
    this.setState({
      selectedCurrency: currency,
    });
  }

  // eslint-disable-next-line react/sort-comp
  assignPlansData(plansData) {
    const { premiumPlansData } = this.state;

    return [SILVER_PLAN, GOLD_PLAN].map((planName) => {
      const plan = premiumPlansData.find((data) => data.current_plan === planName);
      const planInfo = plansData.filter((data) => data.plan_key === planName);

      return {
        ...plan,
        planPriceInfo: plan.planPriceInfo.map((priceInfo) => ({
          ...priceInfo,
          ...planInfo.find((infoItem) => (
            infoItem.billing_period === priceInfo.billing_period
            && infoItem.billing_period_unit === priceInfo.billing_period_unit
            && !infoItem.purchase_identifier?.includes('gbp')
          )),
          gbpPlanInfo: planInfo.find((infoItem) => (
            infoItem.billing_period === priceInfo.billing_period
            && infoItem.billing_period_unit === priceInfo.billing_period_unit
            && infoItem.purchase_identifier?.includes('gbp')
          )),
        })),
      };
    });
  }

  getUserPlan() {
    // eslint-disable-next-line react/prop-types

    Promise.all([SubscriptionsSvc.getCurrentPlan(), SubscriptionsSvc.getPlans()]).then(([{
      key: userPlan,
      name: planName,
      expiration,
    },
    plansData]) => {
      const premiumPlansData = this.assignPlansData(plansData);

      let userPlanType;
      if (!expiration) {
        userPlanType = '2year';
      } else {
        const currentDate = moment();
        const expireDate = moment(expiration);
        if (moment(expireDate).isSameOrBefore(moment(currentDate).add(1, 'M'))) {
          userPlanType = '1month';
        } else if (moment(expireDate).isSameOrBefore(moment(currentDate).add(1, 'y'))) {
          userPlanType = '1year';
        } else {
          userPlanType = '2year';
        }
      }

      this.setState({
        premiumPlansData: [
          freePlan,
          ...premiumPlansData,
        ],
        premiumTablePlansData: [
          planTableHeader,
          freePlan,
          ...premiumPlansData,
        ],
        userPlan,
        userPlanType,
      }, () => {
        const isPremium = [SILVER_PLAN, GOLD_PLAN].includes(userPlan);
        this.getPlanInfo(isPremium, planName);
        this.setState({ loading: false });
      });
    }).catch((e) => {
      console.log(e);
    });
  }

  setActiveFAQid(id) {
    this.setState({ activeFAQid: id });
  }

  getPlanInfo(isPremium, planName) {
    this.setState({
      isPremium,
      planName,
    });
  }

  // for sending user links click to piwik
  sendLinkAction(link, e) {
    const { piwikEnabled } = this.props;

    if (link === 'contact us directly') e.stopPropagation();
    if (piwikEnabled) _paq.push(['trackEvent', 'Premium pricing', link]);
  }

  activeSummaryTab() {
    this.setState({
      isSummaryTabActive: true,
    });
    const { history } = this.props;
    history.push('/pricing#summary');
  }

  activeTableTab() {
    this.setState({
      isSummaryTabActive: false,
    });
    const { history } = this.props;
    history.replace('/pricing#table');
  }

  handlePlanRedirect() {
    const plansSections = document.getElementById('sub_plans_pricing');
    if (!plansSections) return;
    const { width, promobarHeight } = this.props;
    const headerHeight = width > 767 ? 80 : 50;
    const topPosition = plansSections?.getBoundingClientRect()?.top;
    const y = window.pageYOffset + topPosition - headerHeight - 60 - promobarHeight;

    window.scroll({
      top: y,
      behavior: 'smooth',
    });
  }

  render() {
    const {
      activeFAQid,
      isPremium,
      planName,
      isSummaryTabActive,
      userPlan,
      userPlanType,
      premiumPlansData,
      premiumTablePlansData,
      loading,
      currencyOptions,
      selectedCurrency,
    } = this.state;
    const { searchBarActive, actions } = this.props;

    const isUserWithPlan = [SILVER_PLAN, GOLD_PLAN].includes(userPlan);
    return (
      <PriceCurrencyProvider currency={selectedCurrency}>
        <div
          className={
            `${Styles.pricing}
            ${searchBarActive && Styles['blured-background']}
            ${!isSummaryTabActive && Styles.pricingTable}`
          }
        >
          {searchBarActive && <div className={Styles.overlay} /> }
          <h1>
            Subscription Plans Pricing
            <PlayVideoComponent
              videoLink="https://cityfalcon.wistia.com/medias/u6u2dn05wb"
              videoLinkMobile="https://cityfalcon.wistia.com/medias/u6u2dn05wb"
              additionalStyles={Styles['pricing-play-video-btn']}
              tooltipAdditionalContentStyle={Styles['pricing-play-video-btn-tooltip']}
            />
          </h1>
          {isPremium ? (
            <SubscriptionsManagement planName={planName} />
          ) : (
            <div className={`${Styles.pricing_page_header} ${Styles.pricing_text_section}`}>
              <div>
                <p>We have a financial news plan for everyone. They cost as much as air, </p>
                <p>
                  a (really) expensive cup of coffee, or a ticket to the cinema.
                  But for investing success.
                </p>
              </div>
            </div>
          )}
          <div className={Styles.pricing_plan_title}>
            <div className={Styles.pricing_plan_title_text}>STANDARD PLANS</div>
            <div
              className={Styles.pricing_plan_title_btn}
              onClick={this.handlePlanRedirect}
            >
              Select Plan
            </div>
          </div>
          <div className={Styles['layout-filters']}>
            <div className={Styles.currency_choice}>
              <div className={Styles.label}>CURRENCY:</div>
              <CFDropdown
                className={Styles.currency_choice_dropdown}
                options={currencyOptions}
                onSelect={this.setCurrency}
              />
            </div>
            <div className={Styles.pricing_choice}>
              <div className={Styles.label}>LAYOUT:</div>
              <div
                className={`${Styles.pricing_choice_summary} ${isSummaryTabActive ? Styles.pricing_choice_active : ''}`}
                onClick={this.activeSummaryTab}
              >
                <img
                  src={isSummaryTabActive ? iconActiveSummary : iconSummary}
                  alt=""
                  height="16px"
                />
                Summary
              </div>
              <div
                className={`${Styles.pricing_choice_table} ${isSummaryTabActive ? '' : Styles.pricing_choice_active}`}
                onClick={this.activeTableTab}
              >
                <img
                  src={isSummaryTabActive ? iconTable : iconActiveTable}
                  alt=""
                  height="16px"
                />
                Table
              </div>
            </div>
          </div>
          {isSummaryTabActive ? (
            <PricingPlans
              getPlanInfo={this.getPlanInfo}
              userPlan={userPlan}
              userPlanType={userPlanType}
              premiumPlansData={premiumPlansData}
              loading={loading}
              setAuthModalOpened={actions.setAuthModalOpened}
              hasInsightsAccess
            />
          ) : (
            <PricingPlansTable
              getPlanInfo={this.getPlanInfo}
              userPlan={userPlan}
              userPlanType={userPlanType}
              premiumPlansData={premiumTablePlansData}
              loading={loading}
              setAuthModalOpened={actions.setAuthModalOpened}
              hasInsightsAccess
            />
          )}

          <div className={Styles.pricing_add_ons_title}>
            Additional Features and Add Ons
          </div>
          <div className={Styles.pricing_insights_pricing}>
            <InsightsPricing
              userPlan={userPlan}
              isUserWithPlan={isUserWithPlan}
              handlePlanRedirect={this.handlePlanRedirect}
            />
          </div>
          <div className={Styles.faqWrapper}>
            <h1>frequently asked questions</h1>
            {FAQ.map((data) => (
              <FAQComponent
                key={data.id}
                question={data.question}
                answer={data.answer}
                id={data.id}
                activeFAQid={activeFAQid}
                setActiveFAQid={this.setActiveFAQid}
              />
            ))}
          </div>
          <div className={Styles['pricing-footer']}>
            <p>Have more questions? Need more answers?</p>
            <p>
              Visit our &nbsp;
              <a
                onClick={() => this.sendLinkAction('Terms & Conditions page')}
                href="/terms"
              >
                Terms & Conditions page
              </a>
              {' '}
              or &nbsp;
              <a
                onClick={() => this.sendLinkAction('Knowledge Base')}
                href="https://help.cityfalcon.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
              >
                Knowledge Base.
              </a>
            </p>
          </div>
        </div>
      </PriceCurrencyProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  searchBarActive: state.searchReducers.searchBarActive,
  width: state.common.width,
  promobarHeight: state.promobar.height,
  permissions: state.subscriptions.permissions,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    ...authentication,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PricingClient);
